import { useAuth } from "@/features/auth";
import { getInitials } from "@/features/avatar/avatar.utils";
import { getPresenceStatus, usePresenceState } from "@/features/avatar/use-presence";
import { useCreateChatRoom } from "@/features/chat/api/chat-hooks";
import { chatQueries } from "@/features/chat/api/chat-keys";
// Direct module path, not the feature barrel: the barrel re-exports modules
// that mock factories in this feature's tests also stub (see CLAUDE.md).
import { getActivePersona } from "@/features/chat/chat-ai-routing";
import {
  ContainerUpdateForbiddenError,
  SetDefaultAiPersonaError,
} from "@/features/containers/api/containers-api";
import {
  useAddMemberMutation,
  useContainerMembersQuery,
  useSetDefaultAiPersonaMutation,
  useUpdateContainerMutation,
} from "@/features/containers/api/containers-hooks";
import { containerQueries } from "@/features/containers/api/containers-keys";
import { isMemberType } from "@/features/containers/containers.utils";
import { useCreateDocument } from "@/features/docs/api/docs-hooks";
import { docsQueries } from "@/features/docs/api/docs-keys";
import { ProjectRoute } from "@/routes/_authenticated/workspaces/$slug/projects/$projectSlug";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  ContainerLabel,
  ContainerMembersDialogProps,
  ContainerMembersDialogState,
  CurrentMember,
  DefaultPersonaState,
} from "../container-members-dialog.types";
import { getHumanDisplayName, getHumanSubtitle } from "../container-members-dialog.utils";

export function useContainerMembersDialog(
  props: ContainerMembersDialogProps,
): ContainerMembersDialogState {
  const { open, onOpenChange, containerLabel, mode } = props;
  const isCreateMode = mode === "create";
  const manageProps = mode === "manage" ? props : undefined;
  const { logger } = Sentry;
  const { user } = useAuth();
  const { project } = ProjectRoute.useRouteContext();
  const { slug, projectSlug } = ProjectRoute.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const addMember = useAddMemberMutation();
  const updateContainer = useUpdateContainerMutation();
  const createChatMutation = useCreateChatRoom();
  const createDocumentMutation = useCreateDocument();
  const setDefaultAiPersona = useSetDefaultAiPersonaMutation();
  const onlineUsers = usePresenceState();

  // Fetch project members for create mode; cached in manage mode (no extra requests)
  const { data: projectUserMembers } = useContainerMembersQuery(project.id, ["user"]);
  const { data: projectInvitedUserMembers } = useContainerMembersQuery(project.id, [
    "invited_user",
  ]);
  const { data: projectAiMembers } = useContainerMembersQuery(project.id, ["ai"]);

  // ─── State ────────────────────────────────────────────────────────────────
  const [selectedHumanIds, setSelectedHumanIds] = useState<string[]>([]);
  const [selectedAiIds, setSelectedAiIds] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [localIsShared, setLocalIsShared] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const aiInitializedRef = useRef(false);

  useEffect(() => {
    if (open) {
      if (!isCreateMode) {
        setLocalIsShared(manageProps?.isShared ?? false);
      }
    } else {
      setSelectedHumanIds([]);
      setSelectedAiIds([]);
      setErrorMessage(null);
      setLocalIsShared(false);
      setIsCreating(false);
      setIsAdding(false);
      setRemovingMemberId(null);
      aiInitializedRef.current = false;
    }
  }, [open]);

  // Pre-select the Kairo AI persona when opening in create mode. AI Chats are
  // AI-first, so they default to Kairo; Message rooms are human-first (CR-25),
  // so no persona is pre-selected. Runs once per open, once personas load.
  const preselectKairo = containerLabel !== "message";
  useEffect(() => {
    if (!open || !isCreateMode || aiInitializedRef.current || !projectAiMembers) return;
    if (!preselectKairo) {
      aiInitializedRef.current = true;
      return;
    }
    const kairo = projectAiMembers.find((m) => m.ai_persona_display_name.toLowerCase() === "kairo");
    setSelectedAiIds(kairo ? [kairo.ai_persona_id] : []);
    aiInitializedRef.current = true;
  }, [open, isCreateMode, projectAiMembers, preselectKairo]);

  // ─── Derived values ───────────────────────────────────────────────────────
  const isShared = localIsShared;
  // Read-only, no control writes it (CR-177). Live for documents (a generated CR
  // document's AI access): their project personas fold into the roster,
  // display-only. Rooms can no longer carry it (CHECK containers_room_not_ai_shared).
  const isAiShared = !isCreateMode && !!manageProps?.isAiShared;

  const availableHumanMembers = useMemo(() => {
    if (!isCreateMode) return manageProps?.availableHumanMembers ?? [];
    return [
      ...(projectUserMembers?.filter((m) => m.user_id !== user.id) ?? []),
      ...(projectInvitedUserMembers?.filter((m) => m.invite_email) ?? []),
    ];
  }, [isCreateMode, manageProps, projectUserMembers, projectInvitedUserMembers, user.id]);

  const availableAiMembers = useMemo(() => {
    if (!isCreateMode) return manageProps?.availableAiMembers ?? [];
    return projectAiMembers ?? [];
  }, [isCreateMode, manageProps, projectAiMembers]);

  const sortedAvailableHumans = useMemo(
    () =>
      [...availableHumanMembers].sort((a, b) =>
        getHumanDisplayName(a).localeCompare(getHumanDisplayName(b)),
      ),
    [availableHumanMembers],
  );

  const sortedAvailableAi = useMemo(
    () =>
      [...availableAiMembers].sort((a, b) => {
        const aIsKairo = a.ai_persona_display_name.toLowerCase() === "kairo";
        const bIsKairo = b.ai_persona_display_name.toLowerCase() === "kairo";
        if (aIsKairo) return -1;
        if (bIsKairo) return 1;
        return a.ai_persona_display_name.localeCompare(b.ai_persona_display_name);
      }),
    [availableAiMembers],
  );

  const humanSelectionItems = useMemo(
    () =>
      sortedAvailableHumans.map((member) => ({
        id: member.container_member_id,
        label: isMemberType("user")(member)
          ? `${member.profile_first_name ?? ""} ${member.profile_last_name ?? ""}`.trim() ||
            member.profile_email ||
            "Unknown user"
          : isMemberType("invited_user")(member)
            ? member.invite_email || member.invite_domain || "Unknown invited user"
            : "Unknown member",
      })),
    [sortedAvailableHumans],
  );

  // A document with is_ai_shared already folds every project persona in, so
  // none of them is offered again as addable.
  const aiSelectionItems = useMemo(
    () =>
      (isAiShared ? [] : sortedAvailableAi).map((member) => ({
        id: member.ai_persona_id,
        label: member.ai_persona_display_name || "Unknown AI assistant",
      })),
    [isAiShared, sortedAvailableAi],
  );

  // The personas that actually participate in this container: the whole project
  // roster when it is AI-shared (those personas have NO container_members row on
  // the container itself), the explicit AI members otherwise. This is the list
  // the send path resolves the default against, so the marker and the routing
  // agree (CR-46).
  const participatingAiMembers = useMemo(
    () => (isAiShared ? (projectAiMembers ?? []) : (manageProps?.aiMembers ?? [])),
    [isAiShared, projectAiMembers, manageProps],
  );

  const currentMembers = useMemo((): CurrentMember[] => {
    if (isCreateMode) return [];

    // Users and personas fold in the full project roster INDEPENDENTLY: all
    // users when isShared, all personas when isAiShared. Explicit members keep
    // their real container_member_id/role so they stay removable; folded-in
    // members are display-only (removable: false).
    const explicitHumanById = new Map(
      (manageProps?.humanMembers ?? []).filter(isMemberType("user")).map((m) => [m.user_id, m]),
    );
    const explicitAiById = new Map((manageProps?.aiMembers ?? []).map((m) => [m.ai_persona_id, m]));

    const humanSource = isShared
      ? [
          ...(projectUserMembers ?? []),
          ...(projectInvitedUserMembers?.filter((m) => m.invite_email) ?? []),
        ]
      : (manageProps?.humanMembers ?? []);

    const human = humanSource.map((member) => {
      const explicit = isMemberType("user")(member)
        ? explicitHumanById.get(member.user_id)
        : undefined;
      const name = getHumanDisplayName(member);
      return {
        id: explicit?.container_member_id ?? member.container_member_id,
        name,
        subtitle: getHumanSubtitle(member),
        initials: getInitials({ fullName: name }),
        avatarPath: isMemberType("user")(member) ? member.profile_avatar_url || null : null,
        activityStatus: getPresenceStatus(member, onlineUsers),
        me: isMemberType("user")(member) && member.user_id === user.id,
        isOwner: (isShared ? (explicit?.role ?? member.role) : member.role) === "owner",
        removable: isShared ? !!explicit : true,
        aiPersonaId: null,
      };
    });

    const ai = participatingAiMembers.map((member) => {
      const explicit = explicitAiById.get(member.ai_persona_id);
      return {
        id: explicit?.container_member_id ?? member.container_member_id,
        name: member.ai_persona_display_name,
        subtitle: "AI assistant" as const,
        initials: getInitials({ fullName: member.ai_persona_display_name }),
        avatarPath: member.ai_persona_avatar_url || null,
        activityStatus: getPresenceStatus(member, onlineUsers),
        me: false,
        isOwner: (isAiShared ? (explicit?.role ?? member.role) : member.role) === "owner",
        removable: isAiShared ? !!explicit : true,
        aiPersonaId: member.ai_persona_id,
      };
    });

    return [...human, ...ai].sort((a, b) => a.name.localeCompare(b.name));
  }, [
    isCreateMode,
    isShared,
    isAiShared,
    manageProps,
    participatingAiMembers,
    projectUserMembers,
    projectInvitedUserMembers,
    onlineUsers,
    user.id,
  ]);

  // ─── Default AI persona (CR-46) ───────────────────────────────────────────
  // Only AI Chats route un-mentioned messages, so only they get the marker.
  const showDefaultAiPersonaMarker = !isCreateMode && !!manageProps?.isAiChat;
  const storedDefaultAiPersonaId = manageProps?.defaultAiPersonaId ?? null;

  // Same resolution as the send path: the stored id wins while it still points
  // at a participating persona, otherwise the kairo/first fallback does. The
  // star marks this EFFECTIVE default (CR-177): one filled star, always the
  // assistant that will actually answer, whether stored or fallen back to.
  const effectiveDefaultAiPersonaId = showDefaultAiPersonaMarker
    ? (getActivePersona(participatingAiMembers, storedDefaultAiPersonaId)?.ai_persona_id ?? null)
    : null;

  const getDefaultPersonaState = (aiPersonaId: string): DefaultPersonaState =>
    aiPersonaId === effectiveDefaultAiPersonaId ? "default" : "none";

  // Sighted users see the star move; this is the same information for everyone
  // else, including a change another member made.
  const effectiveDefaultName = participatingAiMembers.find(
    (member) => member.ai_persona_id === effectiveDefaultAiPersonaId,
  )?.ai_persona_display_name;

  const defaultAiPersonaStatus = !showDefaultAiPersonaMarker
    ? null
    : !effectiveDefaultName
      ? "No assistant answers when no one is mentioned"
      : `Default assistant (answers when no one is mentioned): ${effectiveDefaultName}`;

  // There is no realtime subscription on `containers`, so a default set by
  // another member is not pushed. Refetch the room row whenever the dialog
  // opens — the moment the user is about to look at it.
  // CR-177: for every container type, not only AI chats — the sharing switch
  // must not show a value another member already changed.
  useEffect(() => {
    if (!open || !manageProps?.containerId) return;
    void queryClient.invalidateQueries({
      queryKey: containerQueries.byId(manageProps.containerId).queryKey,
    });
  }, [open, manageProps?.containerId]);

  // Re-seed the switch when that refetch (or any other writer) changes the
  // stored value while the dialog is open; our own toggle patches the same
  // cache optimistically, so it is skipped while in flight.
  const storedIsShared = manageProps?.isShared;
  useEffect(() => {
    if (!open || storedIsShared === undefined || updateContainer.isPending) return;
    setLocalIsShared(storedIsShared);
  }, [open, storedIsShared, updateContainer.isPending]);

  const handleToggleDefaultAiPersona = (aiPersonaId: string) => {
    if (!manageProps || setDefaultAiPersona.isPending) return;
    // CR-177 (Q3): a chat always has exactly one default, so clicking the
    // starred row never clears it. It is a pure no-op unless the star sits on
    // the fallback while a STALE id is stored (that persona left the chat):
    // then the click pins the starred persona, so re-adding the old one later
    // does not silently move the default back.
    const hasStaleStoredDefault =
      storedDefaultAiPersonaId !== null && storedDefaultAiPersonaId !== effectiveDefaultAiPersonaId;
    if (aiPersonaId === effectiveDefaultAiPersonaId && !hasStaleStoredDefault) return;
    setErrorMessage(null);
    setDefaultAiPersona.mutate(
      { containerId: manageProps.containerId, aiPersonaId },
      {
        // The API layer throws machine-readable reasons because it sits outside
        // wuchale's extraction; the user-facing wording is chosen here, where it
        // is extracted and translated.
        onError: (error) => {
          Sentry.captureException(error);
          const reason = error instanceof SetDefaultAiPersonaError ? error.reason : "unknown";
          setErrorMessage(
            reason === "not_accessible"
              ? "This chat could not be found, or you no longer have access to it."
              : reason === "invalid_request"
                ? "Only AI chats can have an assistant that answers when no one is mentioned."
                : "Could not change which assistant answers. Please try again.",
          );
        },
      },
    );
  };

  const isBusy = isCreateMode
    ? isCreating
    : isAdding || !!removingMemberId || updateContainer.isPending;

  const canAdd = selectedHumanIds.length > 0 || selectedAiIds.length > 0;

  // Owner-only here and in the database (CR-205, see ContainerUpdateForbiddenError).
  const isCurrentUserOwner = isCreateMode
    ? true
    : (manageProps?.humanMembers ?? []).some(
        (m) => isMemberType("user")(m) && m.user_id === user.id && m.role === "owner",
      );

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const invalidateContainerQueries = async () => {
    if (containerLabel === "document") {
      await queryClient.invalidateQueries({
        queryKey: docsQueries.byContainer(project.id).queryKey,
      });
    } else if (containerLabel === "message") {
      await queryClient.invalidateQueries({
        queryKey: chatQueries.messageRoomsByParent(project.id).queryKey,
      });
    } else {
      await queryClient.invalidateQueries({
        queryKey: chatQueries.roomsByParent(project.id).queryKey,
      });
    }
  };

  const addSelectedMembers = async (containerId: string) => {
    // When humans are shared, their picker is hidden — skip any stale selection
    // so we never add explicit members that are already folded in. Personas
    // are always explicit members (CR-177), so their selection always applies.
    const selectedHumans = isShared
      ? []
      : availableHumanMembers.filter((m) => selectedHumanIds.includes(m.container_member_id));
    const selectedAis = availableAiMembers.filter((m) => selectedAiIds.includes(m.ai_persona_id));
    await Promise.all([
      ...selectedHumans.map((member) => {
        if (isMemberType("user")(member)) {
          return addMember.mutateAsync({
            p_container_id: containerId,
            p_user_id: member.user_id,
            p_role: "admin",
          });
        }
        return addMember.mutateAsync({
          p_container_id: containerId,
          p_invite_id: member.invite_id,
          p_role: "admin",
        });
      }),
      ...selectedAis.map((member) =>
        addMember.mutateAsync({
          p_container_id: containerId,
          p_ai_persona_id: member.ai_persona_id,
        }),
      ),
    ]);
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleToggleShared = (nextIsShared: boolean) => {
    setLocalIsShared(nextIsShared);
    if (isCreateMode) return;
    updateContainer.mutate(
      {
        containerId: manageProps!.containerId,
        updates: { is_shared: nextIsShared },
      },
      {
        // CR-97: an unedited message room is titled "All project members" while
        // shared, so the sidebar / Messages list must re-resolve on toggle (the
        // header already re-resolves from containerQueries.byId).
        onSuccess: () => {
          if (containerLabel === "message") void invalidateContainerQueries();
        },
        onError: (error) => {
          setLocalIsShared(!nextIsShared);
          // CR-205: an expected refusal (ownership changed while the dialog
          // was open), so it is explained rather than reported. One literal per
          // label so every word is translated.
          if (error instanceof ContainerUpdateForbiddenError) {
            const ownerOnlyMessage: Record<ContainerLabel, string> = {
              document: "Only the owner can change who this document is shared with.",
              message: "Only the owner can change who this message room is shared with.",
              chat: "Only the owner can change who this AI chat is shared with.",
            };
            setErrorMessage(ownerOnlyMessage[containerLabel]);
            return;
          }
          Sentry.captureException(error);
          setErrorMessage(
            error instanceof Error ? error.message : `Failed to update ${containerLabel} sharing.`,
          );
        },
      },
    );
  };

  const handleCreate = async () => {
    if (isCreating) return;
    setErrorMessage(null);
    setIsCreating(true);
    try {
      let newContainer: { id: string };
      if (containerLabel === "document") {
        newContainer = await createDocumentMutation.mutateAsync({
          p_name: "Untitled document",
          p_parent_container_id: project.id,
          p_is_shared: localIsShared,
        });
      } else {
        const isMessageRoom = containerLabel === "message";
        newContainer = await createChatMutation.mutateAsync({
          parentContainerId: project.id,
          input: {
            // CR-97: a message room starts UNEDITED — an empty stored name —
            // and shows its derived title until a user types one.
            name: isMessageRoom ? "" : "Untitled chat",
            is_shared: localIsShared,
            container_type: isMessageRoom ? "message_room" : "room",
          },
        });
      }

      // addSelectedMembers self-gates against a shared human class, so any
      // remaining selection is safe to add regardless of the switch.
      if (canAdd) {
        try {
          await addSelectedMembers(newContainer.id);
        } catch (memberError) {
          logger.error(`Failed to add members while creating ${containerLabel}`, {
            projectId: project.id,
            containerId: newContainer.id,
          });
          Sentry.captureException(memberError);
          setErrorMessage(
            `Created ${containerLabel}, but failed to add some members. You can try adding them again later.`,
          );
          return;
        }
      }

      onOpenChange(false);
      if (containerLabel === "document") {
        await navigate({
          to: "/workspaces/$slug/projects/$projectSlug/docs/$docId",
          params: { slug, projectSlug, docId: newContainer.id },
          search: { mentionUserId: undefined },
        });
      } else {
        // AI Chats and Message rooms share the room-detail route.
        await navigate({
          to: "/workspaces/$slug/projects/$projectSlug/chats/$chatId",
          params: { slug, projectSlug, chatId: newContainer.id },
          search: { messageId: undefined, draft: undefined },
        });
      }
    } catch (error) {
      logger.error(`Failed to create ${containerLabel}`, {
        projectId: project.id,
      });
      Sentry.captureException(error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : `Failed to create ${containerLabel}. Please try again.`,
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleAddSelected = async () => {
    if (!canAdd || isBusy) return;
    setErrorMessage(null);
    setIsAdding(true);
    try {
      await addSelectedMembers(manageProps!.containerId);
      setSelectedHumanIds([]);
      setSelectedAiIds([]);
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage(error instanceof Error ? error.message : "Failed to add members.");
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveMember = async (containerMemberId: string, redirectToProject?: boolean) => {
    if (isBusy) return;
    setErrorMessage(null);
    setRemovingMemberId(containerMemberId);
    try {
      await manageProps!.onRemoveMember(containerMemberId);
      if (redirectToProject) {
        await invalidateContainerQueries();
        await navigate({
          to: "/workspaces/$slug/projects/$projectSlug",
          params: { slug, projectSlug },
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage(error instanceof Error ? error.message : "Failed to remove member.");
    } finally {
      setRemovingMemberId(null);
    }
  };

  return {
    isShared,
    isCurrentUserOwner,
    handleToggleShared,
    selectedHumanIds,
    setSelectedHumanIds,
    selectedAiIds,
    setSelectedAiIds,
    humanSelectionItems,
    aiSelectionItems,
    canAdd,
    currentMembers,
    showDefaultAiPersonaMarker,
    getDefaultPersonaState,
    handleToggleDefaultAiPersona,
    defaultAiPersonaStatus,
    isBusy,
    errorMessage,
    isCreating,
    handleCreate,
    isAdding,
    handleAddSelected,
    removingMemberId,
    handleRemoveMember,
  };
}
